import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TrendingUp, Shield, Zap, PieChart, Target, BookOpen, Wallet, CreditCard, BarChart3, Lock, Globe, Mail, Github, Linkedin, Users, Swords, Rocket } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Logo from '../components/Logo';
import AppLoader from '../components/AppLoader';

// --- Custom CSS for High-Performance 3D Interactions ---
const customStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  :root {
    --bg-navy: #020617;
    --slate-grey: #94A3B8;
  }

  body {
    background-color: var(--bg-navy);
    font-family: 'Inter', sans-serif;
    overflow-x: hidden;
  }

  /* --- 3D Card Tilt & Spotlight --- */
  .tilt-card {
    position: relative;
    transform-style: preserve-3d;
    transform: perspective(1000px);
    transition: transform 0.1s ease-out; /* Super smooth raw mouse follow */
    will-change: transform;
  }

  /* Inner content layer for 3D depth */
  .tilt-content {
    transform: translateZ(50px);
    transform-style: preserve-3d;
  }

  /* Spotlight Border Effect */
  .spotlight-border {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: radial-gradient(
      800px circle at var(--mouse-x) var(--mouse-y), 
      rgba(255, 255, 255, 0.15), 
      transparent 40%
    );
    z-index: 2;
    mask-image: linear-gradient(#fff, #fff); /* Content mask hack if needed, else z-index works */
  }

  /* Spotlight Background Glow */
  .spotlight-glow {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(
      600px circle at var(--mouse-x) var(--mouse-y), 
      rgba(59, 130, 246, 0.1), 
      transparent 40%
    );
    z-index: 0;
  }

  /* Mobile: Disable complex transforms */
  @media (hover: none) {
    .tilt-card { transform: none !important; }
    .tilt-content { transform: none !important; }
  }

  /* --- Magnetic Button --- */
  .magnetic-btn {
    transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform;
  }

  /* --- Reactive Background Orb --- */
  .cursor-orb {
    position: fixed;
    top: 0; 
    left: 0;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(124, 58, 237, 0.15) 0%, transparent 70%);
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 0;
    border-radius: 50%;
    mix-blend-mode: screen;
    will-change: transform;
    transition: transform 0.15s ease-out; /* Slight lag for fluid feel */
  }

  /* --- Entrance Animations --- */
  .reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1); /* Apple-style ease */
  }

  .reveal.active {
    opacity: 1;
    transform: translateY(0);
  }

  .stagger-1 { transition-delay: 100ms; }
  .stagger-2 { transition-delay: 200ms; }
  .stagger-3 { transition-delay: 300ms; }
  .stagger-4 { transition-delay: 400ms; }

  /* --- Dragon Text Effect --- */
  .dragon-text {
    background: linear-gradient(90deg, #fff 0%, #fff 35%, #a855f7 45%, #d946ef 50%, #a855f7 55%, #fff 65%, #fff 100%);
    background-size: 300% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: dragon-glide 4s infinite linear;
  }

  @keyframes dragon-glide {
    0% { background-position: 150% center; }
    100% { background-position: -150% center; }
  }

  .dragon-trail {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100px;
    height: 4px;
    background: linear-gradient(to right, transparent, #a855f7, #d946ef, #a855f7, transparent);
    border-radius: 50%;
    filter: blur(4px);
    animation: dragon-swoosh 4s infinite linear;
    pointer-events: none;
    z-index: 20;
    opacity: 0.6;
  }

  @keyframes dragon-swoosh {
    0% { left: -20%; transform: translateY(-50%) skewX(-45deg); opacity: 0; }
    10% { opacity: 0.8; }
    90% { opacity: 0.8; }
    100% { left: 120%; transform: translateY(-50%) skewX(-45deg); opacity: 0; }
  }

  /* --- Zigzag Timeline & Dragon Path --- */
  .timeline-container {
    position: relative;
    padding: 80px 0;
  }

  .zigzag-path {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }

  .dragon-follower {
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%); /* Optimized centering */
    width: 20px;
    height: 20px;
    z-index: 40; /* Above checkpoints */
    animation: travel-timeline 8s infinite linear;
    pointer-events: none;
  }

  @keyframes travel-timeline {
    0% { top: 5%; opacity: 0; }
    5% { opacity: 1; }
    95% { opacity: 1; }
    100% { top: 95%; opacity: 0; }
  }

  /* Checkpoint glow animation */
  .checkpoint-node {
    transition: box-shadow 0.3s ease, border-color 0.3s ease;
    will-change: box-shadow;
  }

  .checkpoint-glow {
    animation: checkpoint-pulse 0.6s ease-out;
  }

  @keyframes checkpoint-pulse {
    0% { 
      box-shadow: 0 0 8px rgba(168, 85, 247, 0.1);
    }
    50% { 
      box-shadow: 0 0 25px rgba(168, 85, 247, 0.8), 0 0 40px rgba(168, 85, 247, 0.4), 0 0 60px rgba(168, 85, 247, 0.2);
    }
    100% { 
      box-shadow: 0 0 15px rgba(168, 85, 247, 0.3);
    }
  }



  .dragon-head {
    width: 40px;
    height: 40px;
    background: radial-gradient(circle at 30% 30%, #fff, #a855f7, #7000ff);
    border-radius: 50%;
    box-shadow: 
      0 0 20px #a855f7,
      0 0 40px #7000ff,
      -20px 0 30px rgba(168, 85, 247, 0.4);
    position: relative;
  }

  .dragon-head::after {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    width: 100px;
    height: 20px;
    background: linear-gradient(to right, transparent, rgba(168, 85, 247, 0.6));
    filter: blur(10px);
  }

  .flare {
    position: absolute;
    width: 2px;
    height: 100px;
    background: linear-gradient(to top, transparent, #7000ff, transparent);
    animation: flare-move 3s linear infinite;
    opacity: 0.5;
  }

  @keyframes flare-move {
    0% { transform: translateY(100%) scaleY(0.5); opacity: 0; }
    50% { opacity: 0.8; }
    100% { transform: translateY(-100%) scaleY(1.5); opacity: 0; }
  }
`;

// --- Timeline Features Component with Checkpoint Glow ---
const TimelineFeatures = () => {
    const checkpointRefs = useRef([]);
    const containerRef = useRef(null);

    const features = [
        { icon: <PieChart size={32} />, title: "Expense Tracking", desc: "Visualize spending with interactive charts.", color: "text-purple-400", border: "border-purple-500/30", glow: "#a855f7" },
        { icon: <Target size={32} />, title: "Goal Tracking", desc: "Set financial milestones and monitor progress.", color: "text-emerald-400", border: "border-emerald-500/30", glow: "#10b981" },
        { icon: <BarChart3 size={32} />, title: "Investment Portfolio", desc: "Track investments with market data.", color: "text-blue-400", border: "border-blue-500/30", glow: "#3b82f6" },
        { icon: <Shield size={32} />, title: "Secure Data", desc: "Industry-standard encryption for your peace of mind.", color: "text-rose-400", border: "border-rose-500/30", glow: "#f43f5e" },
        { icon: <Zap size={32} />, title: "AI Advisor", desc: "Intelligent insights for financial decisions.", color: "text-amber-400", border: "border-amber-500/30", glow: "#f59e0b" },
        { icon: <Wallet size={32} />, title: "Reminders & Alerts", desc: "Never miss a bill with automated alerts.", color: "text-indigo-400", border: "border-indigo-500/30", glow: "#6366f1" },
        { icon: <CreditCard size={32} />, title: "Loan Manager", desc: "Optimize debt payoff strategies.", color: "text-cyan-400", border: "border-cyan-500/30", glow: "#06b6d4" },
        { icon: <BookOpen size={32} />, title: "Financial Literacy", desc: "Build your financial knowledge base.", color: "text-teal-400", border: "border-teal-500/30", glow: "#14b8a6" },
        { icon: <Wallet size={32} />, title: "Income Tracker", desc: "Log diverse income sources easily.", color: "text-emerald-500", border: "border-emerald-500/30", glow: "#10b981" },
        { icon: <Globe size={32} />, title: "Global News", desc: "Stay ahead with real-time market updates.", color: "text-blue-500", border: "border-blue-500/30", glow: "#3b82f6" }
    ];

    useEffect(() => {
        let raf;
        const glowingStates = new Array(features.length).fill(false);

        const checkIntersections = () => {
            const follower = document.querySelector('.dragon-follower');
            if (!follower) {
                raf = requestAnimationFrame(checkIntersections);
                return;
            }

            const followerRect = follower.getBoundingClientRect();
            // Calculate center point of the traveling bubble
            const followerY = followerRect.top + followerRect.height / 2;

            checkpointRefs.current.forEach((el, index) => {
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const elementY = rect.top + rect.height / 2;

                // Check distance between bubble center and checkpoint center
                const distance = Math.abs(followerY - elementY);

                // If within 40px (the bubble is passing right over it)
                if (distance < 40 && !glowingStates[index]) {
                    glowingStates[index] = true;
                    el.classList.add('checkpoint-glow');

                    // Remove glow after strictly 600ms (duration of the pulse animation)
                    setTimeout(() => {
                        glowingStates[index] = false;
                        if (el) el.classList.remove('checkpoint-glow');
                    }, 600);
                }
            });

            raf = requestAnimationFrame(checkIntersections);
        };

        raf = requestAnimationFrame(checkIntersections);
        return () => cancelAnimationFrame(raf);
    }, [features.length]);

    return (
        <div ref={containerRef} className="space-y-6 relative z-10">
            {features.map((feat, i) => (
                <div key={i} className={`flex flex-col lg:flex-row items-center justify-between relative reveal ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Card Container */}
                    <div className="w-full lg:w-[42%] px-4">
                        <TiltCard
                            icon={feat.icon}
                            title={feat.title}
                            desc={feat.desc}
                            color={feat.color}
                            border={feat.border}
                            glow={feat.glow}
                            delay={`stagger-${(i % 4) + 1}`}
                        />
                    </div>

                    {/* Central Node (Bubble Point) */}
                    <div
                        ref={el => checkpointRefs.current[i] = el}
                        className={`checkpoint-node hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#020617] border border-purple-500/20 items-center justify-center z-25 shadow-[0_0_8px_rgba(168,85,247,0.1)] pointer-events-none`}
                    >
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shadow-[0_0_6px_#a855f7]"></div>
                    </div>

                    {/* Spacer for alternating layout */}
                    <div className="hidden lg:block w-[42%]"></div>
                </div>
            ))}
        </div>
    );
};

const Landing = () => {

    const navigate = useNavigate();
    const [isRedirecting, setIsRedirecting] = useState(false);

    // Mouse Tracking for Global Orb
    const [orbPos, setOrbPos] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const handleMouseMove = (e) => {
            setOrbPos({ x: e.clientX, y: e.clientY });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Auth Logic
    useEffect(() => {
        const hasToken = window.location.hash.includes('access_token');
        if (hasToken) setIsRedirecting(true);
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) navigate('/dashboard');
        });
    }, [navigate]);

    // Scroll Observer
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) entry.target.classList.add('active');
            });
        }, { threshold: 0.1 });
        document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    if (isRedirecting) return (
        <AppLoader text="VERIFYING SECURITY TOKENS..." />
    );

    return (
        <div className="min-h-screen bg-[#020617] text-slate-300 font-sans relative overflow-hidden selection:bg-purple-500/30 selection:text-white">
            <style>{customStyles}</style>

            {/* 1. Reactive Background Orb (Follows Mouse) */}
            <div
                className="cursor-orb"
                style={{ transform: `translate3d(${orbPos.x - 300}px, ${orbPos.y - 300}px, 0)` }}
            ></div>

            {/* --- Navbar --- */}
            <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#020617]/90 backdrop-blur-xl transition-all duration-300">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            {/* Glow background for logo */}
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-purple-900/20 rounded-full blur-md"></div>
                            <img
                                src="/logo.jpg"
                                alt="FinAssure Logo"
                                className="relative w-14 h-14 object-cover rounded-full border border-purple-500/30 p-1"
                                style={{
                                    filter: 'brightness(1.4) contrast(1.1) drop-shadow(0 0 20px rgba(168, 85, 247, 0.9)) drop-shadow(0 0 10px rgba(168, 85, 247, 0.6))',
                                    background: 'radial-gradient(circle, rgba(168,85,247,0.15), transparent 70%)',
                                }}
                            />
                        </div>
                        <span className="text-xl font-bold text-white tracking-tight">FinAssure</span>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-6">
                        <button
                            onClick={() => {
                                const el = document.getElementById('features');
                                const offset = 100;
                                const bodyRect = document.body.getBoundingClientRect().top;
                                const elementRect = el.getBoundingClientRect().top;
                                const elementPosition = elementRect - bodyRect;
                                const offsetPosition = elementPosition - offset;
                                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                            }}
                            className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
                        >
                            Features
                        </button>
                        <button
                            onClick={() => {
                                const el = document.getElementById('contact');
                                const offset = 100;
                                const bodyRect = document.body.getBoundingClientRect().top;
                                const elementRect = el.getBoundingClientRect().top;
                                const elementPosition = elementRect - bodyRect;
                                const offsetPosition = elementPosition - offset;
                                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                            }}
                            className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
                        >
                            Contact Us
                        </button>
                        <Link to="/login" className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Sign In</Link>
                        <Link to="/register">
                            <Button size="sm" className="bg-gradient-to-r from-purple-900/40 to-black hover:from-purple-800/60 hover:to-slate-900 text-white border border-purple-500/20 rounded-lg transition-all px-5">Get Started</Button>
                        </Link>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="text-slate-400 hover:text-white transition-colors"
                        >
                            {isMobileMenuOpen ? (
                                <X size={24} />
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Content */}
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-white/5 bg-[#020617] p-6 space-y-4 animate-in fade-in slide-in-from-top-4">
                        <button
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                const el = document.getElementById('features');
                                const offset = 100;
                                const bodyRect = document.body.getBoundingClientRect().top;
                                const elementRect = el.getBoundingClientRect().top;
                                const elementPosition = elementRect - bodyRect;
                                const offsetPosition = elementPosition - offset;
                                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                            }}
                            className="block w-full text-left text-lg font-medium text-slate-400 hover:text-white transition-colors"
                        >
                            Features
                        </button>
                        <button
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                const el = document.getElementById('contact');
                                const offset = 100;
                                const bodyRect = document.body.getBoundingClientRect().top;
                                const elementRect = el.getBoundingClientRect().top;
                                const elementPosition = elementRect - bodyRect;
                                const offsetPosition = elementPosition - offset;
                                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                            }}
                            className="block w-full text-left text-lg font-medium text-slate-400 hover:text-white transition-colors"
                        >
                            Contact Us
                        </button>
                        <Link
                            to="/login"
                            className="block text-lg font-medium text-slate-400 hover:text-white transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Sign In
                        </Link>
                        <Link
                            to="/register"
                            className="block"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            <Button className="w-full bg-gradient-to-r from-purple-700 to-black text-white font-bold h-12 rounded-xl border border-purple-500/20 shadow-lg shadow-purple-900/20">
                                Get Started
                            </Button>
                        </Link>
                    </div>
                )}
            </nav>

            {/* --- Hero Section --- */}
            <section className="relative z-10 pt-32 pb-40 px-6 text-center">
                <div className="max-w-5xl mx-auto">
                    <div className="reveal stagger-1 inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full mb-8">
                        <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                        </span>
                        <span className="text-xs font-semibold text-purple-300 tracking-wide uppercase">FinAssure Platform</span>
                    </div>

                    <h1 className="reveal stagger-2 text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight leading-tight">
                        Comprehensive <br />
                        <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(139,92,246,0.3)]">
                            Financial Assurance.
                        </span>
                    </h1>

                    <p className="reveal stagger-3 text-lg text-slate-400 max-w-2xl mx-auto mb-12">
                        Your unified platform for holistic wealth management. Track expenses, monitor investments, manage loans, and access AI-driven insights in a secure, professional dashboard.
                    </p>

                    <div className="reveal stagger-4 flex justify-center mt-8">
                        <MagneticButton>
                            <Link to="/register">
                                <Button size="lg" className="h-16 px-12 bg-gradient-to-r from-purple-900 via-purple-950 to-black hover:from-purple-800 hover:to-slate-950 text-white text-lg font-bold rounded-full shadow-[0_0_40px_rgba(126,34,206,0.5)] border border-purple-500/20 transition-all transform hover:scale-105 uppercase tracking-widest">
                                    Peep Into Our Creation <TrendingUp className="ml-2" size={20} />
                                </Button>
                            </Link>
                        </MagneticButton>
                    </div>
                </div>
            </section>

            {/* --- Compact Vertical Features Timeline (Zoomed Out) --- */}
            <section id="features" className="relative z-10 py-20 px-6 lg:px-12 overflow-hidden">
                <div className="max-w-7xl mx-auto relative text-center">
                    <div className="mb-12 reveal">
                        <h2 className="text-3xl md:text-4xl font-black text-white mb-3 uppercase tracking-tighter">Building Blocks of Our Creation</h2>
                        <p className="text-slate-400 text-sm max-w-xl mx-auto">Engineered for architectural supremacy and absolute financial dominance.</p>
                    </div>

                    <div className="relative">
                        {/* Central Vertical Line */}
                        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[1.5px] bg-gradient-to-b from-purple-500/0 via-purple-500/40 to-purple-500/0 hidden lg:block"></div>

                        {/* Animated Follower Bubble - Travels through all checkpoints */}
                        <div className="dragon-follower hidden lg:block">
                            <div className="w-5 h-5 rounded-full bg-purple-500 border-2 border-[#020617] shadow-[0_0_20px_#a855f7]"></div>
                        </div>

                        <TimelineFeatures />
                    </div>
                </div>
            </section>

            {/* --- Contact Section --- */}
            <section id="contact" className="relative z-10 py-20 px-6 lg:px-8 bg-slate-900/20 border-t border-white/5 overflow-hidden">
                {/* Background Flares */}
                <div className="flare left-[20%] top-0"></div>
                <div className="flare right-[25%] bottom-0"></div>

                <div className="max-w-2xl mx-auto text-center reveal">
                    <div className="relative inline-block mb-10 overflow-visible">
                        <h2 className="text-3xl md:text-4xl font-bold text-white dragon-text tracking-tight relative z-10">
                            Warriors of Creation
                        </h2>
                        <div className="dragon-trail"></div>
                        <div className="absolute -inset-4 bg-purple-600/10 blur-2xl -z-10 animate-pulse"></div>
                    </div>

                    <div className="relative bg-[#020617] p-6 md:p-8 rounded-[30px] border border-purple-500/15 shadow-2xl overflow-hidden">
                        {/* Vertical Timeline Line - Centered through sword icons */}
                        <div className="absolute left-[2.75rem] md:left-[3.25rem] top-8 bottom-8 w-px bg-gradient-to-b from-purple-500/0 via-purple-500/40 to-purple-500/0 hidden sm:block"></div>

                        <div className="space-y-6 relative z-10">
                            {/* Member 1: Pranav */}
                            <div className="flex items-center gap-6 md:gap-8 group">
                                <div className="hidden sm:flex shrink-0 w-10 h-10 bg-[#020617] border border-purple-500/40 rounded-full items-center justify-center relative z-20 group-hover:scale-110 group-hover:border-purple-400 transition-all shadow-lg">
                                    <Swords size={16} className="text-purple-400" />
                                </div>
                                <div className="text-left flex-1 pl-4 sm:pl-0">
                                    <div className="flex items-center justify-between gap-4">
                                        <h3 className="text-base md:text-lg font-semibold text-white group-hover:text-purple-200 transition-colors">Pranav Sanjay Chaudhari</h3>
                                        <div className="flex items-center gap-2">
                                            <a href="mailto:pranavchaudhari2006@gmail.com" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-purple-400 hover:bg-purple-500/10 transition-all border border-white/5" title="Email">
                                                <Mail size={12} />
                                            </a>
                                            <a href="https://www.linkedin.com/in/pranav-chaudhari-b59428328/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all border border-white/5" title="LinkedIn">
                                                <Linkedin size={12} />
                                            </a>
                                            <a href="https://github.com/Pranav-chaudhari-2006" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/5" title="GitHub">
                                                <Github size={12} />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Member 2: Aahil */}
                            <div className="flex items-center gap-6 md:gap-8 group">
                                <div className="hidden sm:flex shrink-0 w-10 h-10 bg-[#020617] border border-purple-500/40 rounded-full items-center justify-center relative z-20 group-hover:scale-110 group-hover:border-purple-400 transition-all shadow-lg">
                                    <Swords size={16} className="text-purple-400" />
                                </div>
                                <div className="text-left flex-1 pl-4 sm:pl-0">
                                    <div className="flex items-center justify-between gap-4">
                                        <h3 className="text-base md:text-lg font-semibold text-white group-hover:text-purple-200 transition-colors">Aahil Sayed</h3>
                                        <div className="flex items-center gap-2">
                                            <a href="mailto:aahil.sayed24@pccoepune.org" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-purple-400 hover:bg-purple-500/10 transition-all border border-white/5" title="Email">
                                                <Mail size={12} />
                                            </a>
                                            <a href="https://linkedin.com/in/aahil-sayed" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all border border-white/5" title="LinkedIn">
                                                <Linkedin size={12} />
                                            </a>
                                            <a href="https://github.com/aahilsayed062" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/5" title="GitHub">
                                                <Github size={12} />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Member 3: Gaurav */}
                            <div className="flex items-center gap-6 md:gap-8 group">
                                <div className="hidden sm:flex shrink-0 w-10 h-10 bg-[#020617] border border-purple-500/40 rounded-full items-center justify-center relative z-20 group-hover:scale-110 group-hover:border-purple-400 transition-all shadow-lg">
                                    <Swords size={16} className="text-purple-400" />
                                </div>
                                <div className="text-left flex-1 pl-4 sm:pl-0">
                                    <div className="flex items-center justify-between gap-4">
                                        <h3 className="text-base md:text-lg font-semibold text-white group-hover:text-purple-200 transition-colors">Gaurav Jaypatre</h3>
                                        <div className="flex items-center gap-2">
                                            <a href="mailto:gaurav.jaypatre24@pccoepune.org" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-purple-400 hover:bg-purple-500/10 transition-all border border-white/5" title="Email">
                                                <Mail size={12} />
                                            </a>
                                            <a href="https://www.linkedin.com/in/gaurav-jaypatre-36448532b?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all border border-white/5" title="LinkedIn">
                                                <Linkedin size={12} />
                                            </a>
                                            <a href="https://github.com/GauravJaypatre" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/5" title="GitHub">
                                                <Github size={12} />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <footer className="py-12 border-t border-white/5 bg-[#020617] text-center reveal">
                <p className="text-slate-500 text-sm">© {new Date().getFullYear()} FinAssure Platform. All rights reserved.</p>
            </footer>
        </div>
    );
};

// --- Magnetic Button Component ---
const MagneticButton = ({ children }) => {
    const btnRef = useRef(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e) => {
        const { clientX, clientY } = e;
        const { left, top, width, height } = btnRef.current.getBoundingClientRect();
        const centerX = left + width / 2;
        const centerY = top + height / 2;

        // Calculate distance from center
        const dist = Math.sqrt(Math.pow(clientX - centerX, 2) + Math.pow(clientY - centerY, 2));

        // Only attract if within 80px
        if (dist < 80) {
            const x = (clientX - centerX) * 0.15; // Magnetic strength
            const y = (clientY - centerY) * 0.15;
            setPos({ x, y });
        } else {
            setPos({ x: 0, y: 0 });
        }
    };

    const handleMouseLeave = () => setPos({ x: 0, y: 0 });

    return (
        <div
            ref={btnRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="magnetic-btn inline-block"
            style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
        >
            {children}
        </div>
    );
};

// --- 3D Tilt Card Component with Spotlight & Shiny Border ---
const TiltCard = ({ icon, title, desc, color, border, glow, delay }) => {
    const cardRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseMove = (e) => {
        const { left, top, width, height } = cardRef.current.getBoundingClientRect();
        const x = e.clientX - left;
        const y = e.clientY - top;

        // Set CSS vars for spotlight
        cardRef.current.style.setProperty('--mouse-x', `${x}px`);
        cardRef.current.style.setProperty('--mouse-y', `${y}px`);

        // Calculate rotation (Max 15 deg)
        const rotateX = ((y / height) - 0.5) * -15;
        const rotateY = ((x / width) - 0.5) * 15;

        cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
    };

    const handleMouseLeave = () => {
        cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
        setIsHovered(false);
    };

    return (
        <div
            ref={cardRef}
            className={`tilt-card group relative ${delay} reveal`}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={() => setIsHovered(true)}
        >
            {/* Spotlight Border (Outer Glow) - Now colored */}
            <div
                className="spotlight-border opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                    background: `radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), ${glow}40, transparent 40%)`
                }}
            ></div>

            {/* Card Content (Inner Layer) */}
            <div
                className={`relative h-full bg-[#0f172a] rounded-2xl p-5 overflow-hidden z-10 glass-card border ${border} transition-all duration-300 group-hover:bg-slate-900/90`}
                style={{
                    boxShadow: isHovered ? `0 0 20px -5px ${glow}60` : 'none',
                    borderColor: isHovered ? glow : undefined
                }}
            >
                {/* Spotlight Background Glow */}
                <div
                    className="spotlight-glow opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                        background: `radial-gradient(300px circle at var(--mouse-x) var(--mouse-y), ${glow}15, transparent 40%)`
                    }}
                ></div>

                <div className="tilt-content relative z-20 flex flex-col items-start h-full">
                    <div className={`w-10 h-10 bg-slate-800/50 rounded-xl flex items-center justify-center mb-4 shadow-lg backdrop-blur-sm group-hover:scale-110 transition-transform duration-300 ${color} ring-1 ring-white/10`}
                        style={{ boxShadow: isHovered ? `0 0 10px ${glow}30` : 'none' }}>
                        {React.cloneElement(icon, { size: 20 })}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-white transition-colors tracking-tight">{title}</h3>
                    <p className="text-slate-400 leading-tight text-xs group-hover:text-slate-300 transition-colors">
                        {desc}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Landing;
